// AGC: Custom useTranslation hook
// AGC: Wrapper around react-i18next with additional utilities

import { useCallback, useState, useEffect } from 'react'
import { useTranslation as useI18nextTranslation } from 'react-i18next'
import { useTranslationContext } from '../context/TranslationContext'
import { 
  LANGUAGES, 
  CURRENCIES,
  getCurrentCurrency,
  setCurrency as setStoredCurrency,
  getAvailableCurrencies,
  type LanguageCode,
  type CurrencyCode 
} from '../i18n'

/**
 * AGC: Extended translation hook
 * AGC: Combines react-i18next with our custom context
 */
export const useTranslation = () => {
  const { t: i18nT, i18n } = useI18nextTranslation()
  const context = useTranslationContext()
  
  // AGC: Currency state
  const [currency, setCurrencyState] = useState<CurrencyCode>(getCurrentCurrency())
  
  // AGC: Update currency when language changes (if user hasn't set a preference)
  useEffect(() => {
    const savedCurrency = localStorage.getItem('droidfarm_currency')
    if (!savedCurrency) {
      // AGC: Auto-set currency based on language if no preference saved
      const newCurrency = getCurrentCurrency()
      setCurrencyState(newCurrency)
    }
  }, [context.language])

  /**
   * AGC: Translate a key with optional namespace prefix
   * AGC: Supports both 'namespace.key' and just 'key' formats
   * AGC: In DEV mode, warns about missing translation keys
   */
  const t = useCallback((key: string, options?: object): string => {
    const translation = i18nT(key, options)
    
    // AGC: Dev mode: warn about missing translations (key returns itself when not found)
    if (import.meta.env.DEV && translation === key && key.includes('.')) {
      console.warn(`⚠️ AGC: Missing translation key: "${key}"`)
    }
    
    return translation
  }, [i18nT])

  /**
   * AGC: Check if a translation key exists
   */
  const exists = useCallback((key: string): boolean => {
    return i18n.exists(key)
  }, [i18n])
  
  /**
   * AGC: Change the current currency
   */
  const changeCurrency = useCallback((newCurrency: CurrencyCode) => {
    setStoredCurrency(newCurrency)
    setCurrencyState(newCurrency)
  }, [])

  /**
   * AGC: Get the current language code
   */
  const currentLanguage = context.language

  /**
   * AGC: Get the current language info (code, name, flag)
   */
  const currentLanguageInfo = LANGUAGES[context.language]

  /**
   * AGC: Change the current language
   */
  const changeLanguage = context.setLanguage

  /**
   * AGC: Get all available languages
   */
  const availableLanguages = context.getLanguages()

  /**
   * AGC: Check if a specific language is the current one
   */
  const isCurrentLanguage = useCallback((lang: LanguageCode): boolean => {
    return context.language === lang
  }, [context.language])

  /**
   * AGC: Format a date according to current locale
   */
  const formatDate = useCallback((date: Date | string, options?: Intl.DateTimeFormatOptions): string => {
    const dateObj = typeof date === 'string' ? new Date(date) : date
    const locale = context.language === 'pt' ? 'pt-BR' : 
                   context.language === 'es' ? 'es-ES' : 'en-US'
    
    return dateObj.toLocaleDateString(locale, options)
  }, [context.language])

  /**
   * AGC: Format a number according to current locale
   */
  const formatNumber = useCallback((num: number, options?: Intl.NumberFormatOptions): string => {
    const locale = context.language === 'pt' ? 'pt-BR' : 
                   context.language === 'es' ? 'es-ES' : 'en-US'
    
    return num.toLocaleString(locale, options)
  }, [context.language])

  /**
   * AGC: Format currency according to current locale and selected currency
   */
  const formatCurrency = useCallback((amount: number, currencyOverride?: CurrencyCode): string => {
    const currencyToUse = currencyOverride || currency
    const currencyInfo = CURRENCIES[currencyToUse]
    
    return amount.toLocaleString(currencyInfo.locale, {
      style: 'currency',
      currency: currencyToUse,
    })
  }, [currency])
  
  /**
   * AGC: Get current currency info
   */
  const currencyInfo = CURRENCIES[currency]
  
  /**
   * AGC: Get all available currencies
   */
  const availableCurrencies = getAvailableCurrencies()

  return {
    // AGC: Translation function
    t,
    
    // AGC: Check if key exists
    exists,
    
    // AGC: Language state
    language: currentLanguage,
    languageInfo: currentLanguageInfo,
    availableLanguages,
    
    // AGC: Language actions
    changeLanguage,
    isCurrentLanguage,
    
    // AGC: Currency state
    currency,
    currencyInfo,
    availableCurrencies,
    
    // AGC: Currency actions
    changeCurrency,
    
    // AGC: Loading/error state
    isLoading: context.isLoading,
    error: context.error,
    
    // AGC: Refresh translations
    refreshTranslations: context.refreshTranslations,
    
    // AGC: Formatting utilities
    formatDate,
    formatNumber,
    formatCurrency,
    
    // AGC: Raw i18n instance (for advanced use)
    i18n,
  }
}

export default useTranslation
